/**
 * Canvas Performance Optimizations
 * 
 * This module provides performance enhancements for the drawing canvas
 * to handle large pages and many strokes efficiently.
 */

/**
 * RequestAnimationFrame-based redraw throttler
 * Prevents excessive redraws by batching them into animation frames
 */
export class RedrawThrottler {
    constructor(redrawCallback) {
        this.redrawCallback = redrawCallback;
        this.requestId = null;
        this.lastRedrawTime = 0;
        this.minInterval = 16; // ~60 FPS
    }

    requestRedraw() {
        if (this.requestId) {
            return; // Already scheduled
        }

        this.requestId = requestAnimationFrame((timestamp) => {
            this.requestId = null;

            // Throttle to prevent excessive redraws
            if (timestamp - this.lastRedrawTime < this.minInterval) {
                this.requestRedraw(); // Reschedule
                return;
            }

            this.lastRedrawTime = timestamp;
            this.redrawCallback();
        });
    }

    cancel() {
        if (this.requestId) {
            cancelAnimationFrame(this.requestId);
            this.requestId = null;
        }
    }
}

/**
 * Viewport-based culling for infinite mode
 * Only renders strokes visible in the current viewport
 */
export class ViewportCuller {
    constructor(viewportWidth, viewportHeight, padding = 100) {
        this.viewportWidth = viewportWidth;
        this.viewportHeight = viewportHeight;
        this.padding = padding; // Extra padding to avoid pop-in
    }

    updateViewport(width, height, scrollX = 0, scrollY = 0) {
        this.viewportWidth = width;
        this.viewportHeight = height;
        this.scrollX = scrollX || 0;
        this.scrollY = scrollY || 0;
    }

    isStrokeVisible(stroke) {
        if (!stroke || !stroke.points || stroke.points.length === 0) {
            return false;
        }

        // Calculate stroke bounds
        const bounds = this.getStrokeBounds(stroke);
        
        // Check if stroke intersects viewport
        const viewportLeft = this.scrollX - this.padding;
        const viewportRight = this.scrollX + this.viewportWidth + this.padding;
        const viewportTop = this.scrollY - this.padding;
        const viewportBottom = this.scrollY + this.viewportHeight + this.padding;

        return !(
            bounds.right < viewportLeft ||
            bounds.left > viewportRight ||
            bounds.bottom < viewportTop ||
            bounds.top > viewportBottom
        );
    }

    getStrokeBounds(stroke) {
        let minX = Infinity, minY = Infinity;
        let maxX = -Infinity, maxY = -Infinity;

        stroke.points.forEach(point => {
            minX = Math.min(minX, point.x);
            minY = Math.min(minY, point.y);
            maxX = Math.max(maxX, point.x);
            maxY = Math.max(maxY, point.y);
        });

        return {
            left: minX,
            top: minY,
            right: maxX,
            bottom: maxY,
            width: maxX - minX,
            height: maxY - minY
        };
    }

    filterVisibleStrokes(strokes) {
        return strokes.filter(stroke => this.isStrokeVisible(stroke));
    }
}

/**
 * Path caching system
 * Pre-calculates and caches Path2D objects for strokes
 */
export class PathCache {
    constructor(maxSize = 1000) {
        this.cache = new Map();
        this.maxSize = maxSize;
        this.accessOrder = [];
    }

    getKey(stroke) {
        // Create unique key based on stroke properties
        return `${stroke.id}_${stroke.points.length}_${stroke.rotation || 0}`;
    }

    get(stroke) {
        const key = this.getKey(stroke);
        if (this.cache.has(key)) {
            // Update access order for LRU
            this.updateAccessOrder(key);
            return this.cache.get(key);
        }
        return null;
    }

    set(stroke, path) {
        const key = this.getKey(stroke);
        
        // Check cache size and evict if needed
        if (this.cache.size >= this.maxSize && !this.cache.has(key)) {
            this.evictLRU();
        }

        this.cache.set(key, path);
        this.updateAccessOrder(key);
    }

    updateAccessOrder(key) {
        // Remove from current position
        const index = this.accessOrder.indexOf(key);
        if (index > -1) {
            this.accessOrder.splice(index, 1);
        }
        // Add to end (most recently used)
        this.accessOrder.push(key);
    }

    evictLRU() {
        if (this.accessOrder.length === 0) return;
        
        // Remove least recently used
        const keyToRemove = this.accessOrder.shift();
        this.cache.delete(keyToRemove);
    }

    clear() {
        this.cache.clear();
        this.accessOrder = [];
    }

    invalidate(strokeId) {
        // Remove all cached entries for a stroke
        const keysToDelete = [];
        for (const key of this.cache.keys()) {
            if (key.startsWith(`${strokeId}_`)) {
                keysToDelete.push(key);
            }
        }
        keysToDelete.forEach(key => {
            this.cache.delete(key);
            const index = this.accessOrder.indexOf(key);
            if (index > -1) {
                this.accessOrder.splice(index, 1);
            }
        });
    }
}

/**
 * Batch stroke renderer
 * Groups strokes by properties for efficient rendering
 */
export class BatchStrokeRenderer {
    static groupStrokes(strokes) {
        const groups = new Map();

        strokes.forEach(stroke => {
            const key = `${stroke.color}_${stroke.thickness}_${stroke.opacity}`;
            if (!groups.has(key)) {
                groups.set(key, []);
            }
            groups.get(key).push(stroke);
        });

        return groups;
    }

    static renderBatched(ctx, strokes, drawStrokeFunction) {
        const groups = this.groupStrokes(strokes);

        groups.forEach((groupStrokes, key) => {
            // Set context state once for entire group
            if (groupStrokes.length > 0) {
                const firstStroke = groupStrokes[0];
                ctx.save();
                ctx.strokeStyle = firstStroke.color;
                ctx.lineWidth = firstStroke.thickness;
                ctx.globalAlpha = firstStroke.opacity;
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';

                // Draw all strokes in this group
                groupStrokes.forEach(stroke => {
                    drawStrokeFunction(ctx, stroke);
                });

                ctx.restore();
            }
        });
    }
}

/**
 * Progressive rendering for very large pages
 * Renders strokes in chunks to prevent blocking
 */
export class ProgressiveRenderer {
    constructor(chunkSize = 100) {
        this.chunkSize = chunkSize;
        this.isRendering = false;
        this.currentChunk = 0;
        this.cancelRequested = false;
    }

    async renderStrokes(ctx, strokes, drawStrokeFunction, onProgress = null) {
        if (this.isRendering) {
            return false; // Already rendering
        }

        this.isRendering = true;
        this.currentChunk = 0;
        this.cancelRequested = false;

        const totalChunks = Math.ceil(strokes.length / this.chunkSize);

        for (let i = 0; i < totalChunks; i++) {
            if (this.cancelRequested) {
                this.isRendering = false;
                return false;
            }

            const start = i * this.chunkSize;
            const end = Math.min(start + this.chunkSize, strokes.length);
            const chunk = strokes.slice(start, end);

            // Render chunk
            chunk.forEach(stroke => {
                drawStrokeFunction(ctx, stroke);
            });

            // Update progress
            if (onProgress) {
                onProgress((i + 1) / totalChunks);
            }

            // Yield to main thread
            await new Promise(resolve => setTimeout(resolve, 0));
        }

        this.isRendering = false;
        return true;
    }

    cancel() {
        this.cancelRequested = true;
    }
}

/**
 * Performance monitoring utility
 */
export class PerformanceMonitor {
    constructor() {
        this.metrics = {
            frameTime: [],
            strokeCount: [],
            redrawCount: 0
        };
        this.maxSamples = 60; // Keep last 60 samples
    }

    recordFrame(duration, strokeCount) {
        this.metrics.frameTime.push(duration);
        this.metrics.strokeCount.push(strokeCount);
        this.metrics.redrawCount++;

        // Keep only recent samples
        if (this.metrics.frameTime.length > this.maxSamples) {
            this.metrics.frameTime.shift();
            this.metrics.strokeCount.shift();
        }
    }

    getAverageFrameTime() {
        if (this.metrics.frameTime.length === 0) return 0;
        const sum = this.metrics.frameTime.reduce((a, b) => a + b, 0);
        return sum / this.metrics.frameTime.length;
    }

    getFPS() {
        const avgFrameTime = this.getAverageFrameTime();
        return avgFrameTime > 0 ? 1000 / avgFrameTime : 0;
    }

    getReport() {
        return {
            averageFrameTime: this.getAverageFrameTime().toFixed(2) + 'ms',
            fps: this.getFPS().toFixed(1),
            totalRedraws: this.metrics.redrawCount,
            currentStrokeCount: this.metrics.strokeCount[this.metrics.strokeCount.length - 1] || 0
        };
    }

    reset() {
        this.metrics = {
            frameTime: [],
            strokeCount: [],
            redrawCount: 0
        };
    }
}

export default {
    RedrawThrottler,
    ViewportCuller,
    PathCache,
    BatchStrokeRenderer,
    ProgressiveRenderer,
    PerformanceMonitor
};
